<script setup lang="ts">
import { CheckCircle2, CreditCard, X, XCircle } from 'lucide-vue-next';
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import type { MiniAppApi } from '../api';
import { ApiError } from '../api';
import { locale, t } from '../i18n';
import { money } from '../state';
import type { TelegramWebApp } from '../telegram';
import { canPay, offeredPresets, paymentPhase } from '../topUp';
import type { OnlinePaymentView, TopUpClub, TopUpOptions } from '../types';
import FloatingField from './FloatingField.vue';

/**
 * «Поповнити онлайн» (owner, 2026-10-27): the network balance topped up from the phone. The club
 * picked decides only whose monobank the money goes to and whose shift books it — the balance is
 * the network's and spends in any of its clubs. A club with no monobank or a closed shift stays
 * in the list, greyed, with why.
 *
 * The provider's page opens inside Telegram; when the player is back the sheet asks how it went,
 * and keeps asking every few seconds while it is open.
 */
const props = defineProps<{
    open: boolean;
    networkId: number | null;
    api: Pick<MiniAppApi, 'topUpOptions' | 'startTopUp' | 'topUpStatus'>;
    telegram: TelegramWebApp | null;
}>();

const emit = defineEmits<{
    close: [];
    /** The balance changed — the app reads the account again. */
    paid: [];
}>();

const options = ref<TopUpOptions | null>(null);
const loading = ref(false);
const clubId = ref<number | null>(null);
const amount = ref<number | null>(null);
const custom = ref('');
const busy = ref(false);
const error = ref('');
const payment = ref<OnlinePaymentView | null>(null);
let poll: ReturnType<typeof setInterval> | null = null;

const club = computed<TopUpClub | null>(
    () => options.value?.clubs.find((c) => c.id === clubId.value) ?? null,
);
const presets = computed(() =>
    club.value ? offeredPresets(club.value.amounts) : [],
);
const payable = computed(
    () => !!club.value?.available && canPay(club.value.amounts, amount.value),
);
const phase = computed(() =>
    payment.value ? paymentPhase(payment.value.status) : null,
);

/** «Оплата через monobank · Apple Pay, Google Pay, картка» — said for the provider it is. */
const caption = computed(() =>
    club.value?.provider === 'mono'
        ? t('Оплата через monobank · Apple Pay, Google Pay, картка')
        : club.value?.provider_label
          ? t('Оплата через {provider}', {
                provider: club.value.provider_label,
            })
          : '',
);

async function load(): Promise<void> {
    if (props.networkId === null) {
        return;
    }

    loading.value = true;
    error.value = '';

    try {
        options.value = await props.api.topUpOptions(props.networkId);
        clubId.value = options.value.default_club_id;
        pickAmount(presets.value[0] ?? null);
    } catch (caught) {
        error.value =
            caught instanceof ApiError
                ? caught.message
                : t('Не вдалося завантажити. Спробуйте ще раз.');
    } finally {
        loading.value = false;
    }
}

function pickClub(id: number): void {
    clubId.value = id;
    pickAmount(presets.value[0] ?? null);
}

function pickAmount(value: number | null): void {
    amount.value = value;
    custom.value = '';
}

function onCustom(value: string): void {
    custom.value = value.replace(/[^\d.,]/g, '');
    const parsed = Number(custom.value.replace(',', '.'));
    amount.value =
        custom.value === '' || !Number.isFinite(parsed) ? null : parsed;
}

async function pay(): Promise<void> {
    if (!club.value || amount.value === null || !payable.value) {
        return;
    }

    busy.value = true;
    error.value = '';

    try {
        payment.value = await props.api.startTopUp(club.value.id, amount.value);

        openPage();
        startPolling();
    } catch (caught) {
        error.value =
            caught instanceof ApiError
                ? caught.message
                : t('Не вдалося почати оплату. Спробуйте ще раз.');
    } finally {
        busy.value = false;
    }
}

/** The provider's page: inside Telegram, or a new tab outside it. */
function openPage(): void {
    const url = payment.value?.page_url;

    if (!url) {
        return;
    }

    if (props.telegram?.openLink) {
        props.telegram.openLink(url);
    } else {
        window.open(url, '_blank', 'noopener');
    }
}

async function check(): Promise<void> {
    if (!payment.value) {
        return;
    }

    try {
        payment.value = await props.api.topUpStatus(payment.value.id);
    } catch {
        // A dropped ask: the next one tries again.
    }

    if (phase.value !== 'waiting' || payment.value.poll_interval === null) {
        stopPolling();

        if (phase.value === 'paid') {
            emit('paid');
        }
    }
}

function startPolling(): void {
    stopPolling();
    poll = setInterval(check, (payment.value?.poll_interval ?? 3) * 1000);
}

function stopPolling(): void {
    if (poll) {
        clearInterval(poll);
        poll = null;
    }
}

/** Back from the payment page: ask at once rather than at the next tick. */
function onVisible(): void {
    if (document.visibilityState === 'visible' && phase.value === 'waiting') {
        void check();
    }
}

function again(): void {
    stopPolling();
    payment.value = null;
    error.value = '';
}

function close(): void {
    stopPolling();
    emit('close');
}

watch(
    () => props.open,
    (open) => {
        if (open) {
            payment.value = null;
            options.value = null;
            void load();
            document.addEventListener('visibilitychange', onVisible);
        } else {
            stopPolling();
            document.removeEventListener('visibilitychange', onVisible);
        }
    },
);

onBeforeUnmount(() => {
    stopPolling();

    if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisible);
    }
});

function failureText(view: OnlinePaymentView): string {
    if (view.status === 'expired') {
        return t('Час на оплату минув. Гроші не списано.');
    }

    if (view.status === 'cancelled') {
        return t('Оплату скасовано. Гроші не списано.');
    }

    return view.failure_reason
        ? t('Банк відхилив оплату: {reason}', { reason: view.failure_reason })
        : t('Оплата не пройшла. Гроші не списано.');
}
</script>

<template>
    <Transition name="sheet" appear>
        <div v-if="open" class="sheet-layer">
            <button
                class="sheet-scrim"
                type="button"
                :aria-label="t('Закрити')"
                @click="close"
            />
            <section
                class="basic-sheet topup-sheet"
                role="dialog"
                aria-modal="true"
                aria-labelledby="topup-title"
                data-test="topup-sheet"
            >
                <button
                    class="sheet-close"
                    type="button"
                    :aria-label="t('Закрити')"
                    @click="close"
                >
                    <X :size="19" />
                </button>

                <!-- Paid. -->
                <template v-if="payment && phase === 'paid'">
                    <div class="topup-mark topup-mark--ok" aria-hidden="true">
                        <CheckCircle2 :size="34" />
                    </div>
                    <h2 id="topup-title">
                        {{
                            t('Баланс поповнено на {amount} ₴', {
                                amount: money(payment.amount, locale),
                            })
                        }}
                    </h2>
                    <p v-if="payment.club" class="topup-hint">
                        {{ payment.club.name }}
                    </p>
                    <p v-if="payment.balance !== null" class="topup-balance">
                        {{ t('Баланс мережі') }}:
                        <strong>{{ money(payment.balance, locale) }} ₴</strong>
                    </p>
                    <button class="primary-button" type="button" @click="close">
                        {{ t('Готово') }}
                    </button>
                </template>

                <!-- Refused, lapsed or cancelled: why, and once more. -->
                <template v-else-if="payment && phase === 'failed'">
                    <div class="topup-mark topup-mark--bad" aria-hidden="true">
                        <XCircle :size="34" />
                    </div>
                    <h2 id="topup-title">{{ t('Оплата не пройшла') }}</h2>
                    <p class="topup-hint">{{ failureText(payment) }}</p>
                    <button class="primary-button" type="button" @click="again">
                        {{ t('Спробувати ще раз') }}
                    </button>
                </template>

                <!-- The payment page is open: waiting for the answer. -->
                <template v-else-if="payment">
                    <div class="topup-mark" aria-hidden="true">
                        <span class="spinner topup-spinner" />
                    </div>
                    <h2 id="topup-title">{{ t('Очікуємо оплату') }}</h2>
                    <p class="topup-hint">
                        {{
                            t(
                                'Завершіть оплату {amount} ₴ на сторінці monobank. Баланс оновиться сам.',
                                { amount: money(payment.amount, locale) },
                            )
                        }}
                    </p>
                    <button
                        v-if="payment.page_url"
                        class="primary-button"
                        type="button"
                        @click="openPage"
                    >
                        {{ t('Відкрити сторінку оплати ще раз') }}
                    </button>
                    <button class="topup-link" type="button" @click="again">
                        {{ t('Обрати іншу суму') }}
                    </button>
                </template>

                <!-- The form. -->
                <template v-else>
                    <div class="topup-mark" aria-hidden="true">
                        <CreditCard :size="28" :stroke-width="1.8" />
                    </div>
                    <h2 id="topup-title">{{ t('Поповнити онлайн') }}</h2>

                    <div v-if="loading && !options" class="invite-skeleton">
                        <span /><span /><span />
                    </div>

                    <template v-else-if="options">
                        <p class="topup-hint">
                            {{ t('Баланс мережі') }}:
                            <strong
                                >{{ money(options.balance, locale) }} ₴</strong
                            >
                        </p>

                        <!-- The club: only when there is a choice. -->
                        <div
                            v-if="options.clubs.length > 1"
                            class="topup-clubs"
                            data-test="topup-clubs"
                        >
                            <button
                                v-for="c in options.clubs"
                                :key="c.id"
                                type="button"
                                class="topup-club"
                                :class="{
                                    'is-active': c.id === clubId,
                                    'is-off': !c.available,
                                }"
                                @click="pickClub(c.id)"
                            >
                                <span class="topup-club__name">{{
                                    c.name
                                }}</span>
                                <span
                                    v-if="!c.available"
                                    class="topup-club__why"
                                    >{{ c.reason?.message }}</span
                                >
                            </button>
                        </div>

                        <p
                            v-if="club && !club.available"
                            class="form-error topup-why"
                            data-test="topup-unavailable"
                        >
                            {{ club.reason?.message }}
                            <template
                                v-if="options.clubs.some((c) => c.available)"
                            >
                                {{ t('Можна обрати інший клуб.') }}
                            </template>
                        </p>

                        <template v-else-if="club">
                            <div
                                class="topup-amounts"
                                data-test="topup-amounts"
                            >
                                <button
                                    v-for="preset in presets"
                                    :key="preset"
                                    type="button"
                                    class="topup-amount"
                                    :class="{
                                        'is-active':
                                            custom === '' && amount === preset,
                                    }"
                                    @click="pickAmount(preset)"
                                >
                                    {{ money(preset, locale) }} ₴
                                </button>
                            </div>
                            <FloatingField
                                v-if="club.amounts.custom"
                                :model-value="custom"
                                :label="t('Своя сума, ₴')"
                                inputmode="numeric"
                                :error="
                                    custom !== '' &&
                                    amount !== null &&
                                    amount < club.amounts.min
                                        ? t('Мінімальна сума: {min} ₴', {
                                              min: money(
                                                  club.amounts.min,
                                                  locale,
                                              ),
                                          })
                                        : undefined
                                "
                                @update:model-value="onCustom"
                            />
                            <p v-if="club.amounts.min > 0" class="topup-min">
                                {{
                                    t('Мінімальна сума: {min} ₴', {
                                        min: money(club.amounts.min, locale),
                                    })
                                }}
                            </p>

                            <button
                                class="primary-button"
                                type="button"
                                :disabled="!payable || busy"
                                data-test="topup-pay"
                                @click="pay"
                            >
                                <span v-if="busy" class="spinner" />
                                <template v-else>{{
                                    amount !== null && payable
                                        ? t('Оплатити {amount} ₴', {
                                              amount: money(amount, locale),
                                          })
                                        : t('Оплатити')
                                }}</template>
                            </button>
                            <p v-if="caption" class="topup-caption">
                                {{ caption }}
                            </p>
                        </template>
                    </template>

                    <p v-if="error" class="form-error">{{ error }}</p>
                </template>
            </section>
        </div>
    </Transition>
</template>
